// ===========================================
// Integrations API Functions
// Admin Third-Party Integrations Page
// ===========================================

import { apiClient } from '../services/api';
import type {
  ThirdPartyIntegrationSettings,
  ThirdPartyIntegrationSettingsInput,
  IntegrationOverviewResponse,
  GoogleCalendarStats,
  TwilioStats,
  WhatsAppStats,
  IntegrationApiResponse,
  IntegrationProvider,
  IntegrationKeyMetadata,
  IntegrationKeyStatus,
  SetIntegrationKeyInput,
  SetIntegrationKeyResponse,
  VerifyIntegrationKeyInput,
  VerifyIntegrationKeyResponse,
} from '../types/integrations.types';

// ===========================================
// INTEGRATION SETTINGS
// ===========================================

/**
 * Get third-party integration settings
 */
export async function getIntegrationSettings(): Promise<ThirdPartyIntegrationSettings> {
  const response = await apiClient.get<IntegrationApiResponse<ThirdPartyIntegrationSettings>>(
    '/admin/settings/integrations'
  );
  return response.data;
}

/**
 * Update third-party integration settings
 */
export async function updateIntegrationSettings(
  data: ThirdPartyIntegrationSettingsInput
): Promise<ThirdPartyIntegrationSettings> {
  const response = await apiClient.patch<IntegrationApiResponse<ThirdPartyIntegrationSettings>>(
    '/admin/settings/integrations',
    data
  );
  return response.data;
}

/**
 * Reset integration settings to defaults
 */
export async function resetIntegrationSettings(): Promise<ThirdPartyIntegrationSettings> {
  const response = await apiClient.post<IntegrationApiResponse<ThirdPartyIntegrationSettings>>(
    '/admin/settings/integrations/reset'
  );
  return response.data;
}

// ===========================================
// INTEGRATION OVERVIEW
// ===========================================

/**
 * Get aggregated overview of all third-party integrations
 */
export async function getIntegrationOverview(): Promise<IntegrationOverviewResponse> {
  const response = await apiClient.get<IntegrationApiResponse<IntegrationOverviewResponse>>(
    '/admin/integrations/overview'
  );
  return response.data;
}

// ===========================================
// GOOGLE CALENDAR STATS
// ===========================================

/**
 * Get detailed Google Calendar integration statistics
 */
export async function getGoogleCalendarStats(): Promise<GoogleCalendarStats> {
  const response = await apiClient.get<IntegrationApiResponse<GoogleCalendarStats>>(
    '/admin/integrations/google-calendar/stats'
  );
  return response.data;
}

// ===========================================
// TWILIO/SMS STATS
// ===========================================

/**
 * Get detailed Twilio SMS statistics
 */
export async function getTwilioStats(): Promise<TwilioStats> {
  const response = await apiClient.get<IntegrationApiResponse<TwilioStats>>(
    '/admin/integrations/twilio/stats'
  );
  return response.data;
}

// ===========================================
// WHATSAPP STATS (Meta Cloud API)
// ===========================================

/**
 * Get detailed WhatsApp messaging statistics
 */
export async function getWhatsAppStats(): Promise<WhatsAppStats> {
  const response = await apiClient.get<IntegrationApiResponse<WhatsAppStats>>(
    '/admin/integrations/whatsapp/stats'
  );
  return response.data;
}

// ===========================================
// INTEGRATION KEYS (Per-School API Keys)
// ===========================================

/**
 * Get integration key status (per-integration summaries)
 */
export async function getIntegrationKeyStatus(): Promise<IntegrationKeyStatus[]> {
  const response = await apiClient.get<IntegrationApiResponse<IntegrationKeyStatus[]>>(
    '/admin/settings/integration-keys/status'
  );
  return response.data;
}

/**
 * Get integration key metadata (never returns actual values)
 */
export async function getIntegrationKeysMetadata(
  integration?: IntegrationProvider
): Promise<IntegrationKeyMetadata[]> {
  const params = integration ? `?integration=${integration}` : '';
  const response = await apiClient.get<IntegrationApiResponse<IntegrationKeyMetadata[]>>(
    `/admin/settings/integration-keys${params}`
  );
  return response.data;
}

/**
 * Set/update an integration key
 */
export async function setIntegrationKey(
  data: SetIntegrationKeyInput
): Promise<SetIntegrationKeyResponse> {
  const response = await apiClient.put<IntegrationApiResponse<SetIntegrationKeyResponse>>(
    '/admin/settings/integration-keys',
    data
  );
  return response.data;
}

/**
 * Delete an integration key
 */
export async function deleteIntegrationKey(
  integration: IntegrationProvider,
  keyName: string
): Promise<void> {
  await apiClient.delete(
    `/admin/settings/integration-keys/${integration}/${keyName}`
  );
}

/**
 * Verify a stored integration key
 */
export async function verifyIntegrationKey(
  data: VerifyIntegrationKeyInput
): Promise<VerifyIntegrationKeyResponse> {
  const response = await apiClient.post<IntegrationApiResponse<VerifyIntegrationKeyResponse>>(
    '/admin/settings/integration-keys/verify',
    data
  );
  return response.data;
}
